import { BrowserModule, Title } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { NgModule, CUSTOM_ELEMENTS_SCHEMA } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import {APP_BASE_HREF, NgOptimizedImage} from '@angular/common';

import { MatButtonModule } from "@angular/material/button";
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatListModule } from "@angular/material/list";
import { MatTableModule } from "@angular/material/table";

import { MatPaginatorModule } from "@angular/material/paginator";
import { MatSortModule } from "@angular/material/sort";
import { MatTabsModule } from "@angular/material/tabs";
import { MatCardModule } from "@angular/material/card";

import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatAutocompleteModule} from "@angular/material/autocomplete";
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from "@angular/material/form-field";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatMenuModule } from "@angular/material/menu";
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSidenavModule } from '@angular/material/sidenav';

import { MatRadioModule } from "@angular/material/radio";
import { MatSelectModule } from "@angular/material/select";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatNativeDateModule } from "@angular/material/core";
import { CdkTableExporterModule } from "cdk-table-exporter";
import { ClipboardModule} from '@angular/cdk/clipboard';


//import { DataTablesModule } from "angular-datatables";

import { AppRoutingModule } from "@app/app-routing.module";
import { AppComponent } from "@app/app.component";
import { HomeComponent } from "@app/components/home/home.component";
import { UserProfileComponent } from "@app/components/user-profile/user-profile.component";
import { OverageComponent } from "@app/components/overage/overage.component";

import { WorkRequestViewComponent } from "@app/components/work-request-view/work-request-view.component";
import { WorkRequestEditComponent } from "@app/components/work-request-view/work-request-edit.component";
import { ProjectDashboardComponent } from "./components/project-dashboard/project-dashboard.component";

import { SoFilteringMultiSelectComponent } from '@app/shared/so-filtering-multiselect/so-filtering-multiselect';
import { SoSearchInputComponent } from '@app/shared/so-search-input/so-search-input.component';

import { SoTextareaComponent} from '@app/shared/so-textarea-input/so-textarea.component';


import { GlobalService } from "@app-services/global.services";
import { PageNotFoundComponent} from "@app/components/page-not-found/page-not-found.component";

import { MatTableExporterModule } from "mat-table-exporter";

import { DatePipe } from "@angular/common";
import { AlertModule } from "./modules/alert";

import { HTTP_INTERCEPTORS, HttpClientModule } from "@angular/common/http";


import { ToastrModule } from 'ngx-toastr';
//import { QuillModule } from 'ngx-quill'
import { NgxEditorModule } from "ngx-editor";

import { NgbModule } from "@ng-bootstrap/ng-bootstrap";


import {
    IPublicClientApplication,
    PublicClientApplication,
    InteractionType,
} from "@azure/msal-browser";
import {
    MsalInterceptor,
    MsalBroadcastService,
    MsalInterceptorConfiguration,
    MsalModule,
    MsalService,
    MSAL_GUARD_CONFIG,
    MSAL_INSTANCE,
    MSAL_INTERCEPTOR_CONFIG,
    MsalGuardConfiguration,
    MsalRedirectComponent,
    ProtectedResourceScopes,
} from "@azure/msal-angular";

import { msalConfig, loginRequest, protectedResources } from "./auth-config";
import { BaseGuard } from "./guards/base.guard";
import { GroupGuard } from "./guards/group.guard";
import { environment } from "environments/environment";
import { EmailGuard } from "./guards/email.guard";
import { PMGuard } from "./guards/pm.guard";
import { SponsoredGuard } from "./guards/sponsored.guard";

import { NgxSpinnerModule } from "ngx-spinner";
import { NgxChartsModule }from '@swimlane/ngx-charts';

import { ReplacePipe } from "@app-utils/replace-pipe";
import { PrettyJsonPipe } from "./utils/pretty-json";
import { AzureNamePipe } from "./utils/azure-name-pipe";

import { UtcToLocalTimePipe } from "@app-utils/utc-to-local-time.pipe";
import { SafeHtmlPipe } from "@app-utils/safeHtml";
import { UtcConverterService } from "@app-services/utc-converter.service";

import { StatisticsComponent } from "./components/statistics/statistics.component";
import { WorkRequestStatusComponent } from "./components/work-request-view/work-request-status.component";
import { SoInputComponent } from "@app/shared";
import { SoSelectComponent } from "@app/shared";
import { SoDatePickerComponent } from "@app/shared";
import { SoAutoCompleteComponent } from "@app/shared";
import { NonProjectDashboardComponent } from "./components/non-project-dashboard/non-project-dashboard.component";
import { FormControlPipe } from "./utils/form-control.pipe";
import { ApplicationInsightsLoggingService } from "./services/application-insights-logging.service";
import { SoModalComponent } from "@app/shared";
import { SnapshotComponent } from "./components/app-modals/screenshot/snapshot.component";
import { SoMenuWithCheckboxes } from "./shared/so-menu-with-checkboxes/so-menu-with-checkboxes";
import { FeedbackComponent } from "./components/feedback/feedback.component";
import { FeedbackViewComponent } from "./components/feedback/feedback-view.component";



/**
 * Here we pass the configuration parameters to create an MSAL instance.
 * For more info, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/configuration.md
 */
export function MSALInstanceFactory(): IPublicClientApplication {
    return new PublicClientApplication(msalConfig);
}

/**
 * MSAL Angular will automatically retrieve tokens for resources
 * added to protectedResourceMap. For more info, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/initialization.md#get-tokens-for-web-api-calls
 */
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {

    //This map determine which api calls include a bearer tokens

    const protectedResourceMap = new Map<
        string,
        Array<string | ProtectedResourceScopes> | null
    >();

    
    protectedResourceMap.set(
        protectedResources.apiUrl.endpoint,
        protectedResources.apiUrl.scopes
    );
    protectedResourceMap.set(
        protectedResources.apiUrl_www.endpoint,
        protectedResources.apiUrl_www.scopes
    );
    protectedResourceMap.set(
        protectedResources.apiUrl_intra.endpoint,
        protectedResources.apiUrl_intra.scopes
    );

    //protectedResourceMap.set(protectedResources.apiUserProxyList.endpoint, protectedResources.apiUserProxyList.scopes);

    return {
        interactionType: InteractionType.Popup,
        protectedResourceMap,
    };
}

/**
 * Set your default interaction type for MSALGuard here. If you have any
 * additional scopes you want the user to consent upon login, add them here as well.
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
    return {
        interactionType: InteractionType.Redirect,
        authRequest: loginRequest,
    };
}

@NgModule({
    declarations: [
        AppComponent,
        HomeComponent,
        StatisticsComponent,
        UserProfileComponent,
        ProjectDashboardComponent,
        NonProjectDashboardComponent,
        OverageComponent,
        WorkRequestViewComponent,
        WorkRequestEditComponent,
        WorkRequestStatusComponent,
        FeedbackComponent,
        FeedbackViewComponent,
        SnapshotComponent,
        ReplacePipe,
        PrettyJsonPipe,
        AzureNamePipe,
        UtcToLocalTimePipe,
        SafeHtmlPipe,
        PageNotFoundComponent,
    ],
    providers: [
        //https://stackoverflow.com/questions/69094451/what-is-the-difference-between-html-base-href-and-angular-app-base-href
        { provide: APP_BASE_HREF, useValue: environment.baseurl },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true,
        },
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory,
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory,
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory,
        },
        { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'fill' } },
        MsalService,
        BaseGuard,
        GroupGuard,
        EmailGuard,
        PMGuard,
        SponsoredGuard,
        MsalBroadcastService,
        GlobalService,
        ApplicationInsightsLoggingService,
        DatePipe,
        UtcConverterService,
        FormControlPipe,
        Title
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    bootstrap: [AppComponent, MsalRedirectComponent],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        AppRoutingModule,
        MatButtonModule,
        MatButtonToggleModule,
        MatToolbarModule,
        MatListModule,
        MatTableModule,
        MatTooltipModule,
        CdkTableExporterModule,
        ClipboardModule,
        MatPaginatorModule,
        MatExpansionModule,
        MatSortModule,
        MatCardModule,
        MatInputModule,
        MatAutocompleteModule,
        MatFormFieldModule,
        MatCheckboxModule,
        MatIconModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatRadioModule,
        MatSelectModule,
        MatProgressBarModule,
        MatMenuModule,
        MatTabsModule,
        MatSidenavModule,
        HttpClientModule,
        FormsModule,
        ReactiveFormsModule,
        MsalModule,
        AlertModule,
        NgxSpinnerModule,
        MatTableExporterModule,
        SoSearchInputComponent,
        SoFilteringMultiSelectComponent,
        SoTextareaComponent,
        SoInputComponent,
        SoSelectComponent,
        SoAutoCompleteComponent,
        SoDatePickerComponent,
        SoModalComponent,
        SoMenuWithCheckboxes,
   
        NgxEditorModule,
        NgxChartsModule,
        //QuillModule.forRoot(),
        ToastrModule.forRoot({
            timeOut: 10000,
            positionClass: 'toast-bottom-right',
            preventDuplicates: false,
        }),
        FormControlPipe,
        NgOptimizedImage,
        NgbModule,

    ]
})
export class AppModule {}
